import { useEffect, useMemo, useState } from "react";
import { ListChecks, Download } from "lucide-react";
import { api, API } from "@/lib/api";
import { PageShell, EmptyBox } from "@/components/PageShell";
import { LedgerFilters, LedgerTable } from "@/components/LedgerTable";
import { AgencyCoverage } from "@/components/AgencyCoverage";

const PAGE = 100;

export default function ScoringLedger() {
  const [d, setD] = useState(null);
  const [hazard, setHazard] = useState("");
  const [outcome, setOutcome] = useState("");
  const [limit, setLimit] = useState(PAGE);
  useEffect(() => { api.scoringLedger().then(setD).catch(() => setD({ rows: [], counts: {}, labels: {}, total: 0 })); }, []);
  useEffect(() => setLimit(PAGE), [hazard, outcome]);
  const rows = useMemo(() => (d?.rows || []).filter((r) => (!hazard || r.hazard === hazard) && (!outcome || r.outcome === outcome)), [d, hazard, outcome]);
  const csv = `${API}/scoring/ledger.csv?${new URLSearchParams({ ...(hazard && { hazard }), ...(outcome && { outcome }) })}`;
  return (
    <PageShell testid="scoring-page" icon={ListChecks} iconClass="text-lime-300" title="Scoring Ledger"
      intro="Every prediction Vigilo Earth has logged, scored or still open, with the agency evidence that decided it, the river basin and the lead time. Read-only and exportable.">
      {d === null ? (
        <div className="text-[11px] font-mono text-atlas-secondary" data-testid="scoring-loading">Loading…</div>
      ) : d.total ? (
        <div className="flex flex-col gap-5">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="flex gap-10 font-mono" data-testid="scoring-summary">
              <div><div className="text-[9px] uppercase tracking-widest text-atlas-secondary/85">Predictions</div><div className="text-2xl text-white" data-testid="scoring-total">{d.total}</div></div>
              <div><div className="text-[9px] uppercase tracking-widest text-atlas-secondary/85">Hit rate (scored)</div><div className="text-2xl text-atlas-cyan" data-testid="scoring-hit-rate">{d.hit_rate_pct == null ? "—" : `${d.hit_rate_pct}%`}</div></div>
            </div>
            <a href={csv} data-testid="scoring-csv-btn" className="inline-flex items-center gap-2 h-9 px-4 border border-lime-300/50 text-lime-200 text-[11px] font-mono uppercase tracking-widest hover:bg-lime-300/10 transition-colors">
              <Download className="w-3.5 h-3.5" /> Export CSV ({rows.length})
            </a>
          </div>
          <LedgerFilters d={d} hazard={hazard} setHazard={setHazard} outcome={outcome} setOutcome={setOutcome} />
          <LedgerTable rows={rows.slice(0, limit)} />
          {rows.length > limit && (
            <button onClick={() => setLimit(limit + PAGE)} data-testid="scoring-more-btn"
              className="self-center h-9 px-5 border border-white/15 text-[11px] font-mono uppercase tracking-widest text-atlas-secondary hover:text-white hover:border-white/30 transition-colors">
              Show more ({rows.length - limit} left)
            </button>
          )}
          <div className="flex flex-col gap-1 border-t border-white/10 pt-4" data-testid="scoring-sources">
            <div className="text-[9px] font-mono uppercase tracking-widest text-atlas-secondary/85">Independent evidence checked</div>
            {Object.entries(d.sources || {}).map(([k, v]) => (
              <div key={k} className="text-[11px] font-mono text-atlas-secondary"><span className="text-white/80">{k}:</span> {v}</div>
            ))}
            <div className="text-[10px] font-mono text-atlas-secondary/75">* ReliefWeb switches on once its app name is set.</div>
          </div>
          <AgencyCoverage />
          <p className="text-[10px] font-mono text-atlas-secondary/85 max-w-3xl">
            Confirmed (late) counts as a hit. Still elevated calls are watched for 72 hours after the 24-hour reporting grace; with no agency report they become a miss.
          </p>
        </div>
      ) : (
        <EmptyBox testid="scoring-empty" title="No predictions logged yet." text="Calls appear here the moment Vigilo Earth flags them." />
      )}
    </PageShell>
  );
}
